/**
 * ToolExecutionView Component
 * Compact one-line visualization of tool execution
 * Inspired by Grok and Chain of Thought components
 * Shows brief loading animation before check for better UX
 */

import { useState, useEffect } from "react";
import {
  FileText,
  Search,
  Database,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface ToolExecutionViewProps {
  toolName: string;
  args?: Record<string, unknown>;
  result?: unknown;
  isExecuting?: boolean;
  isLast?: boolean; // To know if we should show connector line
  intermediateThought?: string; // Reasoning text that appears after this tool
  index?: number; // Tool index for staggered animation
  totalTools?: number; // Total number of tools for timing
  isStreamActive?: boolean; // Whether the stream is still active (for animation control)
}

/**
 * Get tool category from tool name
 */
const getToolCategory = (toolName: string) => {
  if (toolName.startsWith("reports_")) return "reports";
  if (toolName.startsWith("search_")) return "search";
  if (toolName.startsWith("directory_")) return "directory";
  return "other";
};

/**
 * Get tool display info based on category
 */
const getToolInfo = (toolName: string) => {
  const category = getToolCategory(toolName);

  const categoryInfo = {
    reports: {
      icon: FileText,
      label: "Reporte",
      variant: "info" as const,
      color: "text-blue-600 dark:text-blue-400",
      bgColor: "bg-blue-50 dark:bg-blue-950/30",
    },
    search: {
      icon: Search,
      label: "Búsqueda",
      variant: "success" as const,
      color: "text-green-600 dark:text-green-400",
      bgColor: "bg-green-50 dark:bg-green-950/30",
    },
    directory: {
      icon: Database,
      label: "Directorio",
      variant: "default" as const,
      color: "text-gray-600 dark:text-gray-400",
      bgColor: "bg-gray-50 dark:bg-gray-950/30",
    },
    other: {
      icon: Database,
      label: "Herramienta",
      variant: "default" as const,
      color: "text-gray-600 dark:text-gray-400",
      bgColor: "bg-gray-50 dark:bg-gray-950/30",
    },
  };

  return categoryInfo[category];
};

/**
 * Format tool name for display
 */
const formatToolName = (toolName: string): string => {
  // Remove prefix (reports_, search_, etc.)
  const withoutPrefix = toolName.replace(/^(reports_|search_|directory_)/, "");
  // Replace underscores with spaces and capitalize
  return withoutPrefix
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const ToolExecutionView = ({
  toolName,
  isExecuting = false,
  isLast = false,
  intermediateThought,
  index = 0,
  isStreamActive = false,
}: // totalTools = 1,
ToolExecutionViewProps) => {
  const toolInfo = getToolInfo(toolName);

  // Staggered appearance: each tool appears after the previous one
  const [isVisible, setIsVisible] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    // If stream is not active (finished), show immediately without animation
    if (!isStreamActive) {
      setIsVisible(true);
      setShowLoading(false);
      setHasAnimated(true);
      return;
    }

    // If already animated once, don't animate again
    if (hasAnimated) return;

    // Each tool appears 600ms after the previous one (increased for smoother transitions)
    const staggerDelay = 600;
    const appearanceDelay = index * staggerDelay;

    const appearanceTimer = setTimeout(() => {
      setIsVisible(true);
      setShowLoading(true);
    }, appearanceDelay);

    return () => {
      clearTimeout(appearanceTimer);
    };
  }, [index, isStreamActive, hasAnimated]);

  // Separate effect for loading → check transition
  useEffect(() => {
    if (!isVisible || !isStreamActive || hasAnimated) return;

    // Keep loading for 500ms to show the "thinking" state
    // After 500ms, show check mark
    const loadingTimer = setTimeout(() => {
      setShowLoading(false);
      setHasAnimated(true);
    }, 500);

    return () => {
      clearTimeout(loadingTimer);
    };
  }, [isVisible, isStreamActive, hasAnimated]);

  const isLoading = isExecuting || showLoading;

  // Don't render until visible
  if (!isVisible) return null;

  return (
    <div className={cn("relative", "animate-in fade-in-50 duration-300")}>
      <div className="relative flex items-start gap-3 py-1">
        {/* Icon with connector line */}
        <div className="relative flex-shrink-0">
          {/* Icon circle background */}
          <div
            className={cn(
              "relative z-10 flex h-8 w-8 items-center justify-center rounded-full transition-all duration-300",
              isLoading
                ? "bg-background-100 border-2 border-gradient shadow-md"
                : "bg-background-100/50 border border-border/30"
            )}
          >
            {isLoading ? (
              <Loader2 className={cn("h-4 w-4 animate-spin", toolInfo.color)} />
            ) : (
              <CheckCircle2
                className={cn("h-4 w-4", "text-green-500 dark:text-green-400")}
              />
            )}
          </div>

          {/* Vertical connector line to next step */}
          {(!isLast || intermediateThought) && (
            <div
              className={cn(
                "absolute left-1/2 w-px -translate-x-1/2",
                "top-8",
                intermediateThought ? "h-[calc(100%+0.5rem)]" : "h-6",
                "bg-border/30"
              )}
            />
          )}
        </div>

        {/* Tool name */}
        <div className="flex-1 min-w-0 pt-1">
          <span
            className={cn(
              "text-sm transition-colors duration-300",
              "text-text-secondary font-normal"
            )}
          >
            {formatToolName(toolName)}
          </span>
        </div>
      </div>

      {/* Intermediate thought/reasoning */}
      {intermediateThought && (
        <div className="ml-11 mt-1 mb-2">
          <div className="text-xs text-text-secondary italic bg-background-100/30 rounded px-3 py-2 border-l-2 border-border/50">
            {intermediateThought}
          </div>
        </div>
      )}
    </div>
  );
};
